import React, { useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faVideo } from '@fortawesome/pro-solid-svg-icons/faVideo';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { Panel, PanelHead } from '../../../../components/primitives/Panel';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { addDays } from '../planMode';
import { planDayDate, planDayTitle } from '../rolloutDayCopy';
import { useRollout } from './RolloutContext';
import type { NeedKind, PlanNeed } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { dayAndHourInZone } from './rolloutTime';
import { COUNTDOWN_KEYS } from './useRolloutActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const MARK: Record<NeedKind, { className: string | undefined; icon: typeof faClock }> = {
  failed: { className: styles.needBad, icon: faTriangleExclamation },
  manualPost: { className: styles.needBad, icon: faTriangleExclamation },
  draft: { className: styles.needDr, icon: faClock },
  missing: { className: styles.needMiss, icon: faVideo },
  canvas: { className: styles.needMan, icon: faCheck },
};

const nameOf = (platform: string): string => (isSocialPlatform(platform) ? platformName(platform) : platform);

/** One row of "Needs you": what, when, and the one action. */
const NeedRowComponent: React.FC<{ need: PlanNeed; onCanvas: () => void }> = ({ need, onCanvas }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { actions, timeZone, openStep, canAct, channels } = useRollout();
  const step = need.step === null ? '' : planDayTitle(intl, need.step.day);
  const name = need.channel === null ? '' : nameOf(need.channel.platform);
  let title: string;
  let sub: string;
  let action: React.ReactNode;
  const link = (label: string, onClick: () => void) => (
    <button type="button" className={styles.link} disabled={!canAct} onClick={onClick}>
      {label}
    </button>
  );
  switch (need.kind) {
    case 'failed': {
      const reconnect = channels.some(
        (channel) => channel.platform === need.channel?.platform && channel.needsReconnect
      );
      title = intl.formatMessage(rolloutMessages.needFailed, { step, name });
      sub = need.channel?.post?.failReason ?? '';
      action = reconnect
        ? link(intl.formatMessage(rolloutMessages.reconnect), () => router.push(SMLRoutes.promoteChannels))
        : link(intl.formatMessage(rolloutMessages.open), () => openStep(need.step?.key ?? '', need.channel?.platform));
      break;
    }
    case 'manualPost':
      title = intl.formatMessage(rolloutMessages.needManual, { step, name });
      sub = dayAndHourInZone(intl, need.when, timeZone);
      action = link(intl.formatMessage(rolloutMessages.needPostIt), () =>
        router.push(SMLRoutes.promoteCalendar, { post: need.channel?.post?.uuid ?? '' })
      );
      break;
    case 'draft':
      title = intl.formatMessage(rolloutMessages.needDraft, { step, name });
      sub = dayAndHourInZone(intl, need.when, timeZone);
      action = link(intl.formatMessage(rolloutMessages.needSchedule), () => {
        const uuid = need.channel?.post?.uuid;
        if (uuid !== undefined) {
          actions.launch('needs', [uuid]).catch(console.error);
        }
      });
      break;
    case 'missing': {
      const key = (need.step?.key ?? '') as RolloutDayKey;
      title = intl.formatMessage(rolloutMessages.needMissing, { step });
      sub = intl.formatMessage(rolloutMessages.needMissingBy, { day: planDayDate(intl, addDays(need.when, -1)) });
      action = link(intl.formatMessage(rolloutMessages.needMake), () => {
        const run =
          need.step?.day.asset?.themeId === 'aiReel'
            ? actions.makeStepReel('needs', key)
            : actions.makeAndPlan('needs', COUNTDOWN_KEYS.includes(key) ? COUNTDOWN_KEYS : [key]);
        run.catch(console.error);
      });
      break;
    }
    case 'canvas':
      title = intl.formatMessage(rolloutMessages.canvasTitle);
      sub = intl.formatMessage(rolloutMessages.needCanvasSub);
      action = link(intl.formatMessage(rolloutMessages.canvasHow), onCanvas);
  }
  return (
    <div className={styles.need}>
      <span className={clsx(styles.needMark, MARK[need.kind].className)}>
        <FontAwesomeIcon icon={MARK[need.kind].icon} />
      </span>
      <div>
        <b>{title}</b>
        <small>{sub}</small>
      </div>
      {action}
    </div>
  );
};

const NeedRow = TypedMemo(NeedRowComponent);

/**
 * "Needs you" (design rollout-plan b1, d1): each action one time, oldest first. It replaces the gap
 * box. On a phone it folds into one row with "Show".
 */
const NeedsYouPanelComponent: React.FC<{ needs: PlanNeed[]; onCanvas: () => void }> = ({ needs, onCanvas }) => {
  const intl = useIntl();
  const { actions } = useRollout();
  const [isShown, setIsShown] = useState(false);
  return (
    <Panel as="section" aria-label={intl.formatMessage(rolloutMessages.needsTitle)}>
      <PanelHead
        title={intl.formatMessage(rolloutMessages.needsTitle)}
        subtitle={intl.formatMessage(rolloutMessages.needsSub, { count: needs.length })}
      />
      {needs.length > 0 && (
        <div className={clsx(styles.needsToggle, styles.need)}>
          <span className={clsx(styles.needMark, styles.needBad)}>
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <b>{intl.formatMessage(rolloutMessages.needsPhone, { count: needs.length })}</b>
          <button type="button" className={styles.link} onClick={() => setIsShown((shown) => !shown)}>
            {intl.formatMessage(isShown ? rolloutMessages.needsHide : rolloutMessages.needsShow)}
          </button>
        </div>
      )}
      <div className={clsx(styles.needs, !isShown && styles.needsCollapsed)}>
        {needs.length === 0 && <p className={styles.needsEmpty}>{intl.formatMessage(rolloutMessages.needsNone)}</p>}
        {needs.map((need) => (
          <NeedRow
            key={`${need.kind}-${need.step?.key ?? ''}-${need.channel?.platform ?? ''}`}
            need={need}
            onCanvas={onCanvas}
          />
        ))}
      </div>
      {actions.feedback.needs !== undefined && actions.feedback.needs !== null && (
        <p className={clsx(styles.needsEmpty, actions.feedback.needs.tone === 'danger' && styles.feedbackBad)}>
          {actions.feedback.needs.message}
        </p>
      )}
    </Panel>
  );
};

export const NeedsYouPanel = TypedMemo(NeedsYouPanelComponent);
